import React, { FormEvent, useMemo, useRef, useState, useEffect, useCallback } from 'react'
import { useForm, usePage, router } from '@inertiajs/react'
import * as yup from 'yup'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import AttachFileIcon from '@mui/icons-material/AttachFile'

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface ProjectRef {
  id: number
  name: string
  slug: string
  color: string | null
}

interface BoardWithAgents {
  id: number
  name: string
  slug: string
  color: string | null
  project: ProjectRef
  agents: AgentRef[]
}

interface Props {
  open: boolean
  onClose: () => void
  boards: BoardWithAgents[]
}

const STORAGE_KEY = 'okonomi-new-task-defaults'

function loadDefaults(): { projectId?: number; boardId?: number } {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) return JSON.parse(stored)
  } catch { /* ignore */ }
  return {}
}

function saveDefaults(projectId: number, boardId: number) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ projectId, boardId }))
}

const requiredId = (label: string) =>
  yup.number().transform((value, original) => (original === '' ? undefined : value)).required(`${label} is required`)

const schema = yup.object({
  task: yup.object({
    summary: yup.string().required('Summary is required').max(255, 'Summary must be at most 255 characters'),
    instructions: yup.string().nullable().max(50000, 'Instructions must be at most 50,000 characters'),
    assignee_id: yup.mixed().nullable(),
    requestor_id: requiredId('Requestor'),
    reviewer_id: requiredId('Reviewer'),
  })
})

export function SuperTaskFormDialog({ open, onClose, boards }: Props) {
  const { current_agent } = usePage().props as { current_agent?: { id: number } }

  // Unique projects from boards, sorted alphabetically by name
  const projects = useMemo(() => {
    const seen = new Map<number, ProjectRef>()
    boards.forEach(b => {
      if (!seen.has(b.project.id)) seen.set(b.project.id, b.project)
    })
    return Array.from(seen.values()).sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    )
  }, [boards])

  // Resolve initial defaults from localStorage
  const initialDefaults = useMemo(() => {
    const defaults = loadDefaults()
    // Validate stored project is still accessible
    const validProject = projects.find(p => p.id === defaults.projectId)
    const projectId = validProject?.id || projects[0]?.id || ''
    // Validate stored board belongs to the project and is still accessible
    const projectBoards = boards.filter(b => b.project.id === projectId)
    const validBoard = projectBoards.find(b => b.id === defaults.boardId)
    const boardId = validBoard?.id || projectBoards[0]?.id || ''
    return { projectId, boardId }
  }, [boards, projects])

  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>(initialDefaults.projectId)
  const [selectedBoardId, setSelectedBoardId] = useState<number | ''>(initialDefaults.boardId)
  const [files, setFiles] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Re-initialize when dialog opens (in case boards changed)
  useEffect(() => {
    if (open) {
      const defaults = loadDefaults()
      const validProject = projects.find(p => p.id === defaults.projectId)
      const projectId = validProject?.id || projects[0]?.id || ''
      const projectBoards = boards.filter(b => b.project.id === projectId)
      const validBoard = projectBoards.find(b => b.id === defaults.boardId)
      const boardId = validBoard?.id || projectBoards[0]?.id || ''
      setSelectedProjectId(projectId)
      setSelectedBoardId(boardId)
      setFiles([])
    }
  }, [open, boards, projects])

  function handleAddFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const newFiles = Array.from(e.target.files || [])
    if (newFiles.length > 0) setFiles(prev => [...prev, ...newFiles])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function handleRemoveFile(index: number) {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  // Boards filtered by selected project
  const filteredBoards = useMemo(
    () => boards.filter(b => b.project.id === selectedProjectId),
    [boards, selectedProjectId]
  )

  const selectedBoard = useMemo(
    () => boards.find(b => b.id === selectedBoardId),
    [boards, selectedBoardId]
  )

  const agents = selectedBoard?.agents || []

  const { data, setData, post, processing, errors, setError, clearErrors, reset } = useForm({
    task: {
      summary: '',
      instructions: '',
      assignee_id: '' as number | '',
      requestor_id: current_agent?.id ?? ('' as number | ''),
      reviewer_id: current_agent?.id ?? ('' as number | ''),
    },
  })

  // Helper: pick the best agent value when board changes
  const pickAgentDefault = useCallback((currentValue: number | '', newAgents: AgentRef[]): number | '' => {
    const agentIds = newAgents.map(a => a.id)
    // Keep current value if still valid
    if (currentValue !== '' && agentIds.includes(currentValue as number)) return currentValue
    // Fall back to current_agent if they are a member
    if (current_agent?.id && agentIds.includes(current_agent.id)) return current_agent.id
    // Fall back to first agent
    return newAgents[0]?.id ?? ''
  }, [current_agent])

  // Reset agents when board changes
  useEffect(() => {
    if (selectedBoard) {
      const newAgents = selectedBoard.agents
      const agentIds = newAgents.map(a => a.id)

      const newAssignee = data.task.assignee_id !== '' && !agentIds.includes(data.task.assignee_id as number)
        ? '' : data.task.assignee_id
      const newRequestor = pickAgentDefault(data.task.requestor_id, newAgents)
      const newReviewer = pickAgentDefault(data.task.reviewer_id, newAgents)

      if (newAssignee !== data.task.assignee_id || newRequestor !== data.task.requestor_id || newReviewer !== data.task.reviewer_id) {
        setData('task', {
          ...data.task,
          assignee_id: newAssignee,
          requestor_id: newRequestor,
          reviewer_id: newReviewer,
        })
      }
    }
  }, [selectedBoardId])

  function handleProjectChange(newProjectId: number) {
    setSelectedProjectId(newProjectId)
    // Auto-select first board in the new project
    const newProjectBoards = boards.filter(b => b.project.id === newProjectId)
    const firstBoard = newProjectBoards[0]
    setSelectedBoardId(firstBoard?.id || '')
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    clearErrors()

    if (!selectedBoard) {
      setError('board' as any, 'Please select a board')
      return
    }

    try {
      schema.validateSync(data, { abortEarly: false })
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const validationErrors: Record<string, string> = {}
        err.inner.forEach((e) => {
          if (e.path) validationErrors[e.path] = e.message
        })
        setError(validationErrors)
        return
      }
    }

    const formData = new FormData()
    formData.append('task[summary]', data.task.summary)
    formData.append('task[instructions]', data.task.instructions)
    if (data.task.assignee_id !== '') {
      formData.append('task[assignee_id]', String(data.task.assignee_id))
    }
    formData.append('task[requestor_id]', String(data.task.requestor_id))
    formData.append('task[reviewer_id]', String(data.task.reviewer_id))
    files.forEach(f => formData.append('attachments[]', f))

    setSubmitting(true)
    router.post(`/tasks/boards/${selectedBoard.slug}/tasks`, formData as any, {
      forceFormData: true,
      onSuccess: () => {
        // Save project/board selection for next time
        if (selectedProjectId && selectedBoardId) {
          saveDefaults(selectedProjectId as number, selectedBoardId as number)
        }
        reset()
        setFiles([])
        onClose()
      },
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogTitle>New task</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 2.5 }}>
            <FormControl fullWidth>
              <InputLabel>Project</InputLabel>
              <Select
                value={selectedProjectId}
                label="Project"
                onChange={e => handleProjectChange(e.target.value as number)}
              >
                {projects.map(p => (
                  <MenuItem key={p.id} value={p.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: p.color || '#6B7280', flexShrink: 0 }} />
                      {p.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Board</InputLabel>
              <Select
                value={selectedBoardId}
                label="Board"
                onChange={e => setSelectedBoardId(e.target.value as number)}
              >
                {filteredBoards.map(b => (
                  <MenuItem key={b.id} value={b.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: b.color || '#6B7280', flexShrink: 0 }} />
                      {b.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TextField
            label="Summary"
            fullWidth
            autoFocus
            value={data.task.summary}
            onChange={e => setData('task', { ...data.task, summary: e.target.value })}
            error={'task.summary' in errors}
            helperText={(errors as Record<string, string>)['task.summary']}
            sx={{ mb: 2.5 }}
          />
          <TextField
            label="Instructions"
            fullWidth
            multiline
            rows={4}
            value={data.task.instructions}
            onChange={e => setData('task', { ...data.task, instructions: e.target.value })}
            error={'task.instructions' in errors}
            helperText={(errors as Record<string, string>)['task.instructions']}
            sx={{ mb: 2.5 }}
          />
          <Box sx={{ mb: 2.5 }}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={handleAddFiles}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<AttachFileIcon />}
              onClick={() => fileInputRef.current?.click()}
            >
              Attach files
            </Button>
            {files.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {files.map((f, i) => (
                  <Chip
                    key={`${f.name}-${i}`}
                    label={f.name}
                    size="small"
                    variant="outlined"
                    onDelete={() => handleRemoveFile(i)}
                  />
                ))}
              </Box>
            )}
          </Box>
          <FormControl fullWidth sx={{ mb: 2.5 }}>
            <InputLabel>Assignee (optional)</InputLabel>
            <Select
              value={data.task.assignee_id}
              label="Assignee (optional)"
              onChange={e => setData('task', { ...data.task, assignee_id: e.target.value as number | '' })}
            >
              <MenuItem value="">Unassigned</MenuItem>
              {agents.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2.5 }} error={'task.requestor_id' in errors}>
            <InputLabel>Requestor</InputLabel>
            <Select
              value={data.task.requestor_id}
              label="Requestor"
              onChange={e => setData('task', { ...data.task, requestor_id: e.target.value as number })}
            >
              {agents.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
              ))}
            </Select>
            {'task.requestor_id' in errors && (
              <FormHelperText>{(errors as Record<string, string>)['task.requestor_id']}</FormHelperText>
            )}
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2.5 }} error={'task.reviewer_id' in errors}>
            <InputLabel>Reviewer</InputLabel>
            <Select
              value={data.task.reviewer_id}
              label="Reviewer"
              onChange={e => setData('task', { ...data.task, reviewer_id: e.target.value as number })}
            >
              {agents.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
              ))}
            </Select>
            {'task.reviewer_id' in errors && (
              <FormHelperText>{(errors as Record<string, string>)['task.reviewer_id']}</FormHelperText>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={processing || submitting || !selectedBoardId}>
            Create task
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}
